import { tokenAtom } from '@/atoms/auth';
import { jotaiStore } from '@/atoms/store';
import { baseUrl } from '@/constants';

export class ApiError extends Error {
    status: number;
    data: { message: string; [key: string]: unknown };
    constructor(status: number, responseBody: { message: string; [key: string]: unknown }) {
        super(responseBody.message || 'API Error');
        this.status = status;
        this.data = responseBody;
    }
}

// The browser refuses a `keepalive` request outright (a TypeError, not a
// fallback) once the bodies of ALL keepalive requests in flight pass 64 KiB —
// and a page leave sends several at once. So the budget is shared: a body that
// doesn't fit what's left goes as an ordinary request, which at least still
// leaves while the page lives.
const KEEPALIVE_BUDGET = 60_000;
let keepaliveInFlight = 0;
const keepaliveBytes = (options: RequestInit): number | null => {
    if (!options.keepalive) return null;
    const bytes = typeof options.body === 'string' ? new TextEncoder().encode(options.body).length : 0;
    return keepaliveInFlight + bytes <= KEEPALIVE_BUDGET ? bytes : null;
};

export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = jotaiStore.get(tokenAtom);

    const isFormData = options.body instanceof FormData;
    const headers: HeadersInit = {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
    };

    const reserved = keepaliveBytes(options);
    if (reserved !== null) keepaliveInFlight += reserved;
    const response = await fetch(`${baseUrl}${endpoint}`, {
        ...options,
        keepalive: reserved !== null,
        headers: { ...headers, ...(options.headers as Record<string, string>) },
    }).finally(() => {
        if (reserved !== null) keepaliveInFlight -= reserved;
    });

    if (!response.ok) {
        const body = await response.json().catch(() => ({ message: `API Error: ${response.status} ${response.statusText}` }));
        throw new ApiError(response.status, body);
    }
    return response.json();
}
